import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { catchError, retry, tap, finalize, delay } from 'rxjs/operators';
import {
  FormTemplate,
  CreateFormRequest,
  UpdateFormRequest,
  FormLink,
  FormLinkRequest,
  FormStatus
} from '../models/form.model';
import { environment } from '@src/environments/environment';

/**
 * Query parameters for listing form templates
 */
export interface ListTemplatesParams {
  status?: FormStatus;
  searchText?: string;
  sortField?: 'name' | 'createdAt' | 'updatedAt' | 'version';
  sortOrder?: 'asc' | 'desc';
}

@Injectable({
  providedIn: 'root'
})
export class FormService {
  private apiBase = environment.apiUrl;
  
  // Loading state management
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  constructor(private http: HttpClient) {}

  /**
   * List all form templates for an organization
   * @param organizationId - The organization ID
   * @param params - Optional query parameters for filtering and sorting
   * @returns Observable of form template array
   * @example
   * formService.listTemplates('org-123', { status: 'published', sortField: 'name' })
   */
  listTemplates(organizationId: string, params?: ListTemplatesParams): Observable<FormTemplate[]> {
    console.log('[FormService] listTemplates:', { organizationId, params, timestamp: new Date().toISOString() });
    
    let httpParams = new HttpParams();
    if (params) {
      if (params.status) httpParams = httpParams.set('status', params.status);
      if (params.searchText) httpParams = httpParams.set('search', params.searchText);
      if (params.sortField) httpParams = httpParams.set('sortField', params.sortField);
      if (params.sortOrder) httpParams = httpParams.set('sortOrder', params.sortOrder);
    }
    
    this.loadingSubject.next(true);
    
    return this.http.get<FormTemplate[]>(
      `${this.apiBase}/organization/${organizationId}/forms`,
      { params: httpParams, withCredentials: true }
    ).pipe(
      retry({ count: 2, delay: this.retryStrategy }),
      tap(templates => console.log('[FormService] listTemplates success:', templates.length, 'templates')),
      catchError(err => this.handleError(err, 'listTemplates')),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Get a specific form template by ID (returns all versions)
   * @param organizationId - The organization ID
   * @param templateId - The template ID
   * @returns Observable of form template array (multiple versions may exist)
   * @example
   * formService.getTemplate('org-123', 'template-456').subscribe(templates => {
   *   const draftVersion = templates.find(t => t.status === 'draft');
   * })
   */
  getTemplate(organizationId: string, templateId: string): Observable<FormTemplate[]> {
    console.log('[FormService] getTemplate:', { organizationId, templateId, timestamp: new Date().toISOString() });
    
    this.loadingSubject.next(true);
    
    return this.http.get<FormTemplate[]>(
      `${this.apiBase}/organization/${organizationId}/forms/${templateId}`,
      { withCredentials: true }
    ).pipe(
      retry({ count: 2, delay: this.retryStrategy }),
      tap(template => console.log('[FormService] getTemplate success:', template)),
      catchError(err => this.handleError(err, 'getTemplate')),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Create a new form template as draft
   * @param organizationId - The organization ID
   * @param req - The form template data
   * @returns Observable of new template ID
   * @example
   * formService.createTemplate('org-123', { name: 'My Form', tabs: [...] })
   */
  createTemplate(organizationId: string, req: CreateFormRequest): Observable<string> {
    console.log('[FormService] createTemplate:', { organizationId, name: req.name, timestamp: new Date().toISOString() });
    
    this.loadingSubject.next(true);
    
    return this.http.post(
      `${this.apiBase}/organization/${organizationId}/forms`,
      req,
      { responseType: 'text', withCredentials: true }
    ).pipe(
      tap(id => console.log('[FormService] createTemplate success:', id)),
      catchError(err => this.handleError(err, 'createTemplate')),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Update an existing form template (draft only)
   * @param organizationId - The organization ID
   * @param templateId - The template ID to update
   * @param req - The updated form data
   * @returns Observable of success message
   * @example
   * formService.updateTemplate('org-123', 'template-456', { name: 'Updated Name', tabs: [...] })
   */
  updateTemplate(
    organizationId: string,
    templateId: string,
    req: UpdateFormRequest
  ): Observable<string> {
    console.log('[FormService] updateTemplate:', { organizationId, templateId, timestamp: new Date().toISOString() });
    
    this.loadingSubject.next(true);
    
    return this.http.put(
      `${this.apiBase}/organization/${organizationId}/forms/${templateId}`,
      req,
      { responseType: 'text', withCredentials: true }
    ).pipe(
      tap(() => console.log('[FormService] updateTemplate success')),
      catchError(err => this.handleError(err, 'updateTemplate')),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Publish a form template (makes it immutable)
   * @param organizationId - The organization ID
   * @param templateId - The template ID to publish
   * @returns Observable of version info
   * @example
   * formService.publishTemplate('org-123', 'template-456')
   */
  publishTemplate(organizationId: string, templateId: string): Observable<string> {
    console.log('[FormService] publishTemplate:', { organizationId, templateId, timestamp: new Date().toISOString() });
    
    this.loadingSubject.next(true);
    
    return this.http.post(
      `${this.apiBase}/organization/${organizationId}/forms/${templateId}/publish`,
      {},
      { responseType: 'text', withCredentials: true }
    ).pipe(
      retry({ count: 1, delay: this.retryStrategy }),
      tap(result => console.log('[FormService] publishTemplate success:', result)),
      catchError(err => this.handleError(err, 'publishTemplate')),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Archive a form template (makes it read-only and hidden from active lists)
   * @param organizationId - The organization ID
   * @param templateId - The template ID to archive
   * @returns Observable of success message
   * @example
   * formService.archiveTemplate('org-123', 'template-456')
   */
  archiveTemplate(organizationId: string, templateId: string): Observable<string> {
    console.log('[FormService] archiveTemplate:', { organizationId, templateId, timestamp: new Date().toISOString() });
    
    this.loadingSubject.next(true);
    
    return this.http.post(
      `${this.apiBase}/organization/${organizationId}/forms/${templateId}/archive`,
      {},
      { responseType: 'text', withCredentials: true }
    ).pipe(
      retry({ count: 1, delay: this.retryStrategy }),
      tap(() => console.log('[FormService] archiveTemplate success')),
      catchError(err => this.handleError(err, 'archiveTemplate')),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Create a new draft from a published template
   * @param organizationId - The organization ID
   * @param templateId - The published template ID to create draft from
   * @returns Observable of new draft template ID
   * @example
   * formService.createDraftFromTemplate('org-123', 'template-456')
   */
  createDraftFromTemplate(organizationId: string, templateId: string): Observable<string> {
    console.log('[FormService] createDraftFromTemplate:', { organizationId, templateId, timestamp: new Date().toISOString() });
    
    this.loadingSubject.next(true);
    
    return this.http.post(
      `${this.apiBase}/organization/${organizationId}/forms/${templateId}/create-draft`,
      {},
      { responseType: 'text', withCredentials: true }
    ).pipe(
      tap(newId => console.log('[FormService] createDraftFromTemplate success:', newId)),
      catchError(err => this.handleError(err, 'createDraftFromTemplate')),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  // Form Link operations
  /**
   * Link a form template to a task
   * @param organizationId - The organization ID
   * @param taskId - The task ID to link to
   * @param req - The form link request data
   * @returns Observable of link ID
   */
  linkFormToTask(
    organizationId: string,
    taskId: string,
    req: FormLinkRequest
  ): Observable<string> {
    console.log('[FormService] linkFormToTask:', { organizationId, taskId, timestamp: new Date().toISOString() });
    
    this.loadingSubject.next(true);
    
    return this.http.post(
      `${this.apiBase}/organization/${organizationId}/task/${taskId}/form-link`,
      req,
      { responseType: 'text', withCredentials: true }
    ).pipe(
      tap(linkId => console.log('[FormService] linkFormToTask success:', linkId)),
      catchError(err => this.handleError(err, 'linkFormToTask')),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Link a form template to a project
   * @param organizationId - The organization ID
   * @param projectId - The project ID to link to
   * @param req - The form link request data
   * @returns Observable of link ID
   */
  linkFormToProject(
    organizationId: string,
    projectId: string,
    req: FormLinkRequest
  ): Observable<string> {
    console.log('[FormService] linkFormToProject:', { organizationId, projectId, timestamp: new Date().toISOString() });
    
    this.loadingSubject.next(true);
    
    return this.http.post(
      `${this.apiBase}/organization/${organizationId}/project/${projectId}/form-link`,
      req,
      { responseType: 'text', withCredentials: true }
    ).pipe(
      tap(linkId => console.log('[FormService] linkFormToProject success:', linkId)),
      catchError(err => this.handleError(err, 'linkFormToProject')),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Get all form links for a task
   * @param organizationId - The organization ID
   * @param taskId - The task ID
   * @returns Observable of form link array
   */
  getTaskFormLinks(organizationId: string, taskId: string): Observable<FormLink[]> {
    console.log('[FormService] getTaskFormLinks:', { organizationId, taskId, timestamp: new Date().toISOString() });
    
    this.loadingSubject.next(true);
    
    return this.http.get<FormLink[]>(
      `${this.apiBase}/organization/${organizationId}/task/${taskId}/form-link`,
      { withCredentials: true }
    ).pipe(
      retry({ count: 2, delay: this.retryStrategy }),
      tap(links => console.log('[FormService] getTaskFormLinks success:', links.length, 'links')),
      catchError(err => this.handleError(err, 'getTaskFormLinks')),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Get all form links for a project
   * @param organizationId - The organization ID
   * @param projectId - The project ID
   * @returns Observable of form link array
   */
  getProjectFormLinks(organizationId: string, projectId: string): Observable<FormLink[]> {
    console.log('[FormService] getProjectFormLinks:', { organizationId, projectId, timestamp: new Date().toISOString() });
    
    this.loadingSubject.next(true);
    
    return this.http.get<FormLink[]>(
      `${this.apiBase}/organization/${organizationId}/project/${projectId}/form-link`,
      { withCredentials: true }
    ).pipe(
      retry({ count: 2, delay: this.retryStrategy }),
      tap(links => console.log('[FormService] getProjectFormLinks success:', links.length, 'links')),
      catchError(err => this.handleError(err, 'getProjectFormLinks')),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Handle HTTP errors with user-friendly messages
   * @private
   */
  private handleError(error: HttpErrorResponse | Error | any, operation: string): Observable<never> {
    let errorMessage = 'An unknown error occurred';

    // Interceptors may rethrow plain Error instances without HTTP metadata.
    if (!(error instanceof HttpErrorResponse)) {
      errorMessage = error?.message || errorMessage;
      console.error(`[FormService] ${operation} error:`, {
        status: error?.status,
        message: errorMessage,
        error,
        timestamp: new Date().toISOString()
      });
      return throwError(() => new Error(errorMessage));
    }
    
    if (error.error instanceof ErrorEvent) {
      // Client-side or network error
      errorMessage = `Network error: ${error.error.message}`;
    } else {
      // Backend returned an unsuccessful response code
      switch (error.status) {
        case 400:
          errorMessage = error.error?.message || 'Invalid request. Please check your input.';
          break;
        case 401:
          errorMessage = 'Authentication required. Please log in again.';
          break;
        case 403:
          errorMessage = 'You do not have permission to perform this action.';
          break;
        case 404:
          errorMessage = 'The requested form template was not found.';
          break;
        case 409:
          errorMessage = error.error?.message || 'Conflict: This operation cannot be completed.';
          break;
        case 500:
          errorMessage = 'Server error. Please try again later.';
          break;
        case 503:
          errorMessage = 'Service temporarily unavailable. Please try again in a moment.';
          break;
        default:
          errorMessage = error.error?.message || `Request failed with status ${error.status || 'unknown'}`;
      }
    }
    
    console.error(`[FormService] ${operation} error:`, {
      status: error.status,
      message: errorMessage,
      error: error.error,
      timestamp: new Date().toISOString()
    });
    
    return throwError(() => new Error(errorMessage));
  }

  /**
   * Retry strategy with exponential backoff for transient errors
   * @private
   */
  private retryStrategy = (error: HttpErrorResponse, retryCount: number): Observable<number> => {
    // Don't retry on permanent errors
    if (error.status === 400 || error.status === 401 || error.status === 403 || error.status === 404) {
      throw error;
    }
    
    // Exponential backoff: 1s, 2s, 4s
    const delayMs = Math.pow(2, retryCount) * 1000;
    console.log(`[FormService] Retrying after ${delayMs}ms (attempt ${retryCount + 1})`);
    
    // Return an observable that emits after delay
    return new Observable<number>(subscriber => {
      setTimeout(() => {
        subscriber.next(retryCount);
        subscriber.complete();
      }, delayMs);
    });
  }
}
